import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  getTimetableDetail,
  updateActivitySlot,
  deleteActivitySlot,
  addActivitySlot,
  validateSlotMove,
  propagateSlotChanges,
} from '../../_services/schedules.service';
import { TimetableDetail, ActivitySlot } from '../../_models/schedule.model';
import SlotEditPanel from './components/SlotEditPanel';
import SlotMovePropagationDialog from './components/SlotMovePropagationDialog';
import './styles.css';

type ViewMode = 'list' | 'grid';

// Helper function to get the start of the week (Sunday)
const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.setDate(diff));
};

// Helper function to get the end of the week (Saturday)
const getWeekEnd = (date: Date): Date => {
  const weekStart = getWeekStart(date);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return weekEnd;
};

const ScheduleDetail: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [timetable, setTimetable] = useState<TimetableDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(getWeekStart(new Date()));

  // Slot edit panel state
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<ActivitySlot | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Drag and drop state
  const [draggedSlot, setDraggedSlot] = useState<ActivitySlot | null>(null);
  const [dragOverCell, setDragOverCell] = useState<{ date: string; timeSlot: string } | null>(null);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [showPropagateDialog, setShowPropagateDialog] = useState(false);
  const [pendingMove, setPendingMove] = useState<any>(null);
  const [movingSlot, setMovingSlot] = useState(false);

  useEffect(() => {
    loadTimetableDetail();
  }, [id]);

  // Auto-dismiss toast after 4 seconds
  useEffect(() => {
    if (snackbarMessage) {
      const timer = setTimeout(() => {
        setSnackbarMessage('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [snackbarMessage]);

  const loadTimetableDetail = async () => {
    setLoading(true);
    try {
      const response = await getTimetableDetail(Number(id));

      if (response.success && response.data) {
        setTimetable(response.data);
      } else {
        console.error('Failed to load timetable detail');
        setTimetable(null);
      }
    } catch (error) {
      console.error('Error loading timetable detail:', error);
      setTimetable(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    history.push('/schedules');
  };

  const handleHome = () => {
    history.push('/home');
  };

  const handleSlotClick = (slot: ActivitySlot) => {
    setSelectedSlot(slot);
    setPanelOpen(true);
  };

  const handlePanelClose = () => {
    setPanelOpen(false);
    setSelectedSlot(null);
  };

  const handleSlotSave = async (slotId: number, data: Partial<ActivitySlot>) => {
    try {
      const response = await updateActivitySlot(slotId, data);
      if (response.success) {
        setSnackbarMessage('Slot updated successfully');
        setSnackbarSeverity('success');
        await loadTimetableDetail();
      } else {
        setSnackbarMessage(response.message || 'Failed to update slot');
        setSnackbarSeverity('error');
      }
    } catch (error: any) {
      console.error('Error updating slot:', error);
      setSnackbarMessage(error.message || 'Error updating slot');
      setSnackbarSeverity('error');
    }
  };

  const handleSlotDelete = async (slotId: number) => {
    try {
      const response = await deleteActivitySlot(slotId);
      if (response.success) {
        setSnackbarMessage('Slot deleted successfully');
        setSnackbarSeverity('success');
        await loadTimetableDetail();
      } else {
        setSnackbarMessage(response.message || 'Failed to delete slot');
        setSnackbarSeverity('error');
      }
    } catch (error: any) {
      console.error('Error deleting slot:', error);
      setSnackbarMessage(error.message || 'Error deleting slot');
      setSnackbarSeverity('error');
    }
  };

  const handleSlotAdd = async (data: Partial<ActivitySlot>) => {
    try {
      const response = await addActivitySlot(Number(id), data);
      if (response.success) {
        setSnackbarMessage('Slot added successfully');
        setSnackbarSeverity('success');
        await loadTimetableDetail();
      } else {
        setSnackbarMessage(response.message || 'Failed to add slot');
        setSnackbarSeverity('error');
      }
    } catch (error: any) {
      console.error('Error adding slot:', error);
      setSnackbarMessage(error.message || 'Error adding slot');
      setSnackbarSeverity('error');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, slot: ActivitySlot) => {
    setDraggedSlot(slot);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
    // Add a class to the dragged element
    (e.currentTarget as HTMLElement).style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
    // Don't clear draggedSlot here - we need it for the confirm dialog
    // It will be cleared in handlePropagateDialogClose
    setDragOverCell(null);
  };

  const handleDragOver = (e: React.DragEvent, date: string, timeSlot: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell({ date, timeSlot });
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = async (e: React.DragEvent, date: string, timeSlot: string, startTime: string, endTime: string) => {
    e.preventDefault();
    setDragOverCell(null);

    if (!draggedSlot) return;

    // date is already in YYYY-MM-DD format (raw date from weekDatesRaw)
    const targetDate = date;

    // Check if we're dropping in the same slot
    const slotDate = String(draggedSlot.date);
    const formattedSlotDate = slotDate.includes('T') ? slotDate.split('T')[0] : slotDate.split(' ')[0];

    if (
      formattedSlotDate === targetDate &&
      draggedSlot.startTime === startTime &&
      draggedSlot.endTime === endTime
    ) {
      // Dropped in the same slot - do nothing
      setDraggedSlot(null);
      return;
    }

    // Validate the move
    try {
      const validation = await validateSlotMove(draggedSlot.id!, {
        newDate: targetDate,
        newStartTime: startTime,
        newEndTime: endTime,
        instructorId: draggedSlot.instructorId || undefined,
        roomId: draggedSlot.roomId || undefined,
      });

      if (validation.success && validation.data) {
        setValidationResult(validation.data);

        if (!validation.data.valid) {
          // Show conflicts
          const conflictMessages = validation.data.conflicts
            .map((c: any) => c.message)
            .join('\n');

          const proceed = window.confirm(
            `The following conflicts were detected:\n\n${conflictMessages}\n\nDo you want to proceed anyway?`
          );

          if (!proceed) {
            setDraggedSlot(null);
            return;
          }
        }

        // Ask if user wants to propagate changes
        const moveData = {
          newDate: targetDate,
          newStartTime: startTime,
          newEndTime: endTime,
        };

        setPendingMove(moveData);
        setShowPropagateDialog(true);
      }
    } catch (error: any) {
      console.error('Error validating slot move:', error);
      setSnackbarMessage('Error validating move: ' + error.message);
      setSnackbarSeverity('error');
      setDraggedSlot(null);
    }
  };

  const handlePropagateDialogClose = () => {
    setShowPropagateDialog(false);
    setPendingMove(null);
    setDraggedSlot(null);
  };

  const handleConfirmMove = async (propagate: boolean, weeks?: number) => {
    console.log('handleConfirmMove started', {
      propagate,
      weeks,
      hasDraggedSlot: !!draggedSlot,
      hasPendingMove: !!pendingMove,
      draggedSlot,
      pendingMove
    });

    if (!draggedSlot || !pendingMove) {
      console.error('Cannot move slot: missing draggedSlot or pendingMove');
      return;
    }

    console.log('handleConfirmMove validation passed', { propagate, weeks });

    // Close dialog immediately and show loading overlay instead
    handlePropagateDialogClose();
    setMovingSlot(true);

    try {
      console.log('Starting slot move...', { draggedSlot, pendingMove, propagate, weeks });

      // Update the current slot
      const updateResponse = await updateActivitySlot(draggedSlot.id!, {
        date: pendingMove.newDate,
        startTime: pendingMove.newStartTime,
        endTime: pendingMove.newEndTime,
      });

      console.log('Update response:', updateResponse);

      if (!updateResponse.success) {
        throw new Error(updateResponse.message || 'Failed to update slot');
      }

      // Propagate if requested
      if (propagate) {
        console.log('Starting propagation...', { weeks });
        const propagateResponse = await propagateSlotChanges(draggedSlot.id!, {
          ...pendingMove,
          weeks,
        });

        console.log('Propagate response:', propagateResponse);

        if (propagateResponse.success && propagateResponse.data) {
          setSnackbarMessage(
            `Slot moved and changes propagated to ${propagateResponse.data.updatedCount} downstream slot(s)`
          );
          setSnackbarSeverity('success');
        } else {
          setSnackbarMessage('Slot moved, but propagation failed: ' + (propagateResponse.message || 'Unknown error'));
          setSnackbarSeverity('error');
        }
      } else {
        setSnackbarMessage('Slot moved successfully');
        setSnackbarSeverity('success');
      }

      console.log('Reloading timetable...');
      await loadTimetableDetail();
      console.log('Timetable reloaded');
    } catch (error: any) {
      console.error('Error moving slot:', error);
      setSnackbarMessage('Error moving slot: ' + error.message);
      setSnackbarSeverity('error');
    } finally {
      setMovingSlot(false);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString: string | undefined) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  const getStatusClass = (status: string | undefined) => {
    if (!status) return 'slot-status-scheduled';
    const statusLower = status.toLowerCase();
    if (statusLower === 'cancelled') return 'slot-status-cancelled';
    if (statusLower === 'conducted') return 'slot-status-conducted';
    return 'slot-status-scheduled';
  };

  const groupSlotsByDate = (slots: ActivitySlot[]) => {
    const grouped: { [key: string]: ActivitySlot[] } = {};

    slots.forEach(slot => {
      const dateKey = slot.date ? formatDate(slot.date) : 'No Date';
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(slot);
    });

    // Sort by date (newest first)
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      if (a === 'No Date') return 1;
      if (b === 'No Date') return -1;
      return new Date(b).getTime() - new Date(a).getTime();
    });

    return sortedKeys.map(dateKey => ({
      date: dateKey,
      slots: grouped[dateKey].sort((a, b) => {
        const timeA = a.startTime || '';
        const timeB = b.startTime || '';
        return timeA.localeCompare(timeB);
      }),
    }));
  };

  const renderGridView = (slots: ActivitySlot[]) => {
    // Calculate week boundaries
    const weekEnd = getWeekEnd(currentWeekStart);

    // Filter slots to only include those in the current week
    const weekSlots = slots.filter((slot) => {
      if (!slot.date) return false;
      const slotDate = new Date(slot.date);
      return slotDate >= currentWeekStart && slotDate <= weekEnd;
    });

    // Group slots by timeslot and date
    const gridData: {
      [timeSlot: string]: {
        startTime: string;
        endTime: string;
        slotsByDate: { [date: string]: ActivitySlot[] };
      };
    } = {};

    weekSlots.forEach((slot) => {
      if (!slot.startTime || !slot.endTime || !slot.date) return;

      const timeSlot = `${slot.startTime}-${slot.endTime}`;
      if (!gridData[timeSlot]) {
        gridData[timeSlot] = {
          startTime: slot.startTime,
          endTime: slot.endTime,
          slotsByDate: {},
        };
      }

      const dateKey = formatDate(slot.date);
      if (!gridData[timeSlot].slotsByDate[dateKey]) {
        gridData[timeSlot].slotsByDate[dateKey] = [];
      }

      gridData[timeSlot].slotsByDate[dateKey].push(slot);
    });

    // Get sorted time slots
    const sortedTimeSlots = Object.keys(gridData).sort((a, b) => {
      const timeA = gridData[a].startTime;
      const timeB = gridData[b].startTime;
      return timeA.localeCompare(timeB);
    });

    // Generate all dates for the week (Sun-Sat)
    const weekDates: string[] = [];
    const weekDatesRaw: string[] = []; // Raw YYYY-MM-DD format for drag-drop
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      const rawDate = date.toISOString().split('T')[0];
      weekDatesRaw.push(rawDate);
      weekDates.push(formatDate(rawDate));
    }

    return (
      <div className="grid-view">
        {/* Week Navigation */}
        <div className="week-navigation">
          <button
            className="week-nav-btn"
            onClick={() => {
              const prevWeek = new Date(currentWeekStart);
              prevWeek.setDate(prevWeek.getDate() - 7);
              setCurrentWeekStart(prevWeek);
            }}
          >
            ← Previous Week
          </button>
          <div className="week-display">
            <span className="week-icon">📅</span>
            <span className="week-text">
              {formatDate(currentWeekStart.toISOString().split('T')[0])} - {formatDate(weekEnd.toISOString().split('T')[0])}
            </span>
          </div>
          <button
            className="week-nav-btn"
            onClick={() => {
              const nextWeek = new Date(currentWeekStart);
              nextWeek.setDate(nextWeek.getDate() + 7);
              setCurrentWeekStart(nextWeek);
            }}
          >
            Next Week →
          </button>
        </div>

        <div className="grid-scroll-wrapper">
          <div className="grid-container">
            {/* Header Row */}
            <div className="grid-header">
            <div className="grid-cell time-header">Time Slot</div>
            {weekDates.map((date) => (
              <div key={date} className="grid-cell date-header">
                {date}
              </div>
            ))}
          </div>

          {/* Grid Body */}
          <div className="grid-body">
            {sortedTimeSlots.length > 0 ? (
              sortedTimeSlots.map((timeSlot) => {
                const { startTime, endTime, slotsByDate } = gridData[timeSlot];
                return (
                  <div key={timeSlot} className="grid-row">
                    <div className="grid-cell time-cell">
                      <span className="time-icon">🕐</span>
                      <span className="time-range">
                        {formatTime(startTime)} - {formatTime(endTime)}
                      </span>
                    </div>
                    {weekDates.map((date, dateIndex) => {
                    const rawDate = weekDatesRaw[dateIndex];
                    const slots = slotsByDate[date] || [];
                    const isDragOver = dragOverCell?.date === rawDate && dragOverCell?.timeSlot === timeSlot;
                    return (
                      <div
                        key={date}
                        className={`grid-cell slot-cell ${isDragOver ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, rawDate, timeSlot)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, rawDate, timeSlot, startTime, endTime)}
                      >
                        {slots.map((slot) => (
                          <div
                            key={slot.id}
                            className={`compact-slot-card ${getStatusClass(slot.slotStatus)}`}
                            draggable={true}
                            onDragStart={(e) => handleDragStart(e, slot)}
                            onDragEnd={handleDragEnd}
                            onClick={() => handleSlotClick(slot)}
                            style={{ cursor: 'grab' }}
                            title={`${slot.topicName || slot.name || 'No Topic Specified'}\n${slot.instructorName || ''}\n${slot.roomName || ''}\n${
                              slot.batches?.map((b) => b.batchName).join(', ') || ''
                            }\nDrag to move or click to edit`}
                          >
                            <div className="compact-topic">{slot.topicName || slot.name || 'No Topic'}</div>
                            {slot.instructorName && (
                              <div className="compact-detail">
                                <span className="detail-icon">👤</span>
                                <span className="detail-text">{slot.instructorName}</span>
                              </div>
                            )}
                            {slot.roomName && (
                              <div className="compact-detail">
                                <span className="detail-icon">🏫</span>
                                <span className="detail-text">{slot.roomName}</span>
                              </div>
                            )}
                            {slot.batches && slot.batches.length > 0 && (
                              <div className="compact-batches">
                                {slot.batches.slice(0, 2).map((batch) => (
                                  <span key={batch.id} className="compact-batch-tag">
                                    {batch.batchName}
                                  </span>
                                ))}
                                {slot.batches.length > 2 && (
                                  <span className="compact-batch-more">
                                    +{slot.batches.length - 2}
                                  </span>
                                )}
                              </div>
                            )}
                            {slot.slotStatus && (
                              <span className={`compact-status-badge ${getStatusClass(slot.slotStatus)}`}>
                                {slot.slotStatus}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })
            ) : (
              <div className="grid-empty-state">
                <div className="empty-state-icon">📭</div>
                <p>No activity slots found for this week</p>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="schedule-detail-container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              border: '4px solid var(--border-default)',
              borderTop: '4px solid var(--brand-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto',
            }}
          />
          <p style={{ marginTop: '16px', color: 'var(--text-muted)' }}>Loading timetable details...</p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  if (!timetable) {
    return (
      <div className="schedule-detail-container">
        <div className="empty-state">
          <div className="empty-state-icon">❌</div>
          <h3>Timetable Not Found</h3>
          <p>The requested timetable could not be found.</p>
          <button
            onClick={handleBack}
            style={{
              marginTop: '20px',
              background: 'var(--brand-primary)',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Back to Schedules
          </button>
        </div>
      </div>
    );
  }

  const groupedSlots = timetable.activitySlots ? groupSlotsByDate(timetable.activitySlots) : [];

  return (
    <div className="schedule-detail-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button
          onClick={handleHome}
          style={{
            background: 'var(--brand-primary)',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 12px',
            cursor: 'pointer',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            color: 'white',
          }}
          title="Go to Home"
        >
          🏠
        </button>
        <button
          onClick={handleBack}
          style={{
            background: 'var(--surface-page)',
            border: '1px solid var(--border-default)',
            borderRadius: '6px',
            padding: '8px 12px',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          ← Back to Schedules
        </button>
      </div>

      <div className="schedule-detail-header">
        <h1 style={{ margin: '0 0 20px 0', fontSize: '28px', color: 'var(--text-strong)' }}>
          {timetable.name || 'Untitled Timetable'}
        </h1>

        <div className="schedule-info-grid">
          <div className="info-item">
            <div className="info-label">Effective From</div>
            <div className="info-value">{formatDate(timetable.effectiveFrom)}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Effective To</div>
            <div className="info-value">{formatDate(timetable.effectiveTo)}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Total Cohorts</div>
            <div className="info-value">{timetable.batches?.length || 0}</div>
          </div>
          <div className="info-item">
            <div className="info-label">Activity Slots</div>
            <div className="info-value">{timetable.activitySlots?.length || 0}</div>
          </div>
        </div>

        {timetable.remarks && (
          <div style={{ marginTop: '20px', padding: '16px', background: '#fff5f5', borderLeft: '4px solid #fc8181', borderRadius: '4px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '6px' }}>REMARKS</div>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-body)' }}>{timetable.remarks}</p>
          </div>
        )}
      </div>

      <div className="batches-section">
        <h2 className="section-title">
          <span>📚</span> Mapped Cohorts
        </h2>
        {timetable.batches && timetable.batches.length > 0 ? (
          <div className="batches-grid">
            {timetable.batches.map((batch) => (
              <div key={batch.id} className="batch-chip">
                {batch.batchName || `Cohort ${batch.batchId}`}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">📭</div>
            <p>No cohorts mapped to this timetable</p>
          </div>
        )}
      </div>

      <div className="activity-slots-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 className="section-title" style={{ margin: 0 }}>
            <span>🎯</span> Activity Slots
          </h2>
          <div className="view-mode-selector">
            <button
              onClick={() => setViewMode('list')}
              className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
            >
              <span className="view-icon">📋</span>
              List View
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
            >
              <span className="view-icon">⊞</span>
              Grid View
            </button>
          </div>
        </div>
        {groupedSlots.length > 0 ? (
          viewMode === 'list' ? (
          <div>
            {groupedSlots.map(({ date, slots }) => (
              <div key={date} style={{ marginBottom: '32px' }}>
                <h3 style={{
                  fontSize: '16px',
                  color: 'var(--text-body)',
                  fontWeight: 600,
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '2px solid var(--border-default)'
                }}>
                  {date}
                </h3>
                <div className="activity-slots-list">
                  {slots.map((slot) => (
                    <div
                      key={slot.id}
                      className="activity-slot-card"
                      onClick={() => handleSlotClick(slot)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="slot-bar"></div>
                      <div className="slot-content">
                        <div className="slot-header">
                          <h4 className="slot-name">{slot.topicName || slot.name || 'No Topic Specified'}</h4>
                          {slot.slotStatus && (
                            <span className={`slot-status-badge ${getStatusClass(slot.slotStatus)}`}>
                              {slot.slotStatus}
                            </span>
                          )}
                        </div>

                        <div className="slot-details">
                          <div className="slot-detail-item time">
                            <div className="slot-detail-label">Time</div>
                            <div className="slot-detail-value">{formatTime(slot.startTime)} – {formatTime(slot.endTime)}</div>
                          </div>
                          {slot.instructorName && (
                            <div className="slot-detail-item">
                              <span className="slot-detail-label">Instructor</span>
                              <span className="slot-detail-value">{slot.instructorName}</span>
                            </div>
                          )}
                          {slot.roomName && (
                            <div className="slot-detail-item">
                              <span className="slot-detail-label">Room</span>
                              <span className="slot-detail-value">{slot.roomName}</span>
                            </div>
                          )}
                          {slot.shiftName && (
                            <div className="slot-detail-item">
                              <span className="slot-detail-label">Shift</span>
                              <span className="slot-detail-value">{slot.shiftName}</span>
                            </div>
                          )}
                          {slot.centreName && (
                            <div className="slot-detail-item">
                              <span className="slot-detail-label">Centre</span>
                              <span className="slot-detail-value">{slot.centreName}</span>
                            </div>
                          )}
                        </div>

                        {slot.batches && slot.batches.length > 0 && (
                          <div className="slot-batches">
                            <div className="slot-batches-label">Cohorts</div>
                            <div className="slot-batch-tags">
                              {slot.batches.map((batch, index) => (
                                <span key={index} className="slot-batch-tag">
                                  <span className="cohort-dot"></span>
                                  {batch.batchName || `Cohort ${batch.batchId}`}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {slot.remarks && (
                          <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-soft)', fontStyle: 'italic' }}>
                            {slot.remarks}
                          </div>
                        )}

                        <div className="slot-id">ID: {slot.id}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          ) : (
            renderGridView(timetable.activitySlots || [])
          )
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">📭</div>
            <p>No activity slots found for this timetable</p>
          </div>
        )}
      </div>

      {/* Slot Edit Panel */}
      <SlotEditPanel
        open={panelOpen}
        slot={selectedSlot}
        timetableId={Number(id)}
        onClose={handlePanelClose}
        onSave={handleSlotSave}
        onDelete={handleSlotDelete}
        onAdd={handleSlotAdd}
      />

      {/* Simple Toast Notification */}
      {snackbarMessage && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            background: snackbarSeverity === 'success' ? '#10b981' : '#ef4444',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 10000,
            animation: 'slideInUp 0.3s ease-out',
            maxWidth: '400px',
            fontSize: '14px',
            fontWeight: 500,
          }}
          onClick={() => setSnackbarMessage('')}
        >
          {snackbarMessage}
        </div>
      )}

      {/* Propagation Dialog */}
      <SlotMovePropagationDialog
        open={showPropagateDialog}
        onClose={handlePropagateDialogClose}
        onConfirm={handleConfirmMove}
        slotName={draggedSlot?.topicName || draggedSlot?.name || 'Activity Slot'}
        loading={movingSlot}
      />

      {/* Loading Overlay */}
      {movingSlot && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: '60px',
              height: '60px',
              border: '6px solid rgba(255, 255, 255, 0.3)',
              borderTop: '6px solid white',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <p
            style={{
              color: 'white',
              fontSize: '18px',
              fontWeight: 600,
              marginTop: '24px',
            }}
          >
            Moving slot and propagating changes...
          </p>
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '14px',
              marginTop: '8px',
            }}
          >
            Please wait, this may take a few seconds
          </p>
        </div>
      )}

      {/* Animation keyframes */}
      <style>
        {`
          @keyframes slideInUp {
            from {
              transform: translateY(100px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ScheduleDetail;
